import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Minus } from "lucide-react";
import { useState, useEffect } from "react";

interface AnimalStrain {
  strain_name: string;
  strain_number: string;
  genotype: string;
  quantity: number | "";
  sex: string;
  age_range: string;
  different_age_acceptable: string;
  product_etc: string;
}

const OrderFormStep = ({ onValidChange }: { onValidChange: (isValid: boolean) => void }) => {
  const [institutionAddress, setInstitutionAddress] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [sameAsCustomerInfo, setSameAsCustomerInfo] = useState(false);
  
  // Form field states
  const [piName, setPiName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [phoneNumber1, setPhoneNumber1] = useState("");
  const [phoneNumber2, setPhoneNumber2] = useState("");
  const [phoneNumber3, setPhoneNumber3] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [institutionType, setInstitutionType] = useState("");  // User must select
  
  // Multiple animal strains
  const [animalStrains, setAnimalStrains] = useState<AnimalStrain[]>([{
    strain_name: "",
    strain_number: "",
    genotype: "",
    quantity: "",
    sex: "",
    age_range: "",
    different_age_acceptable: "",
    product_etc: ""
  }]);
  
  const [deliveryDate, setDeliveryDate] = useState("");
  const [partialShipment, setPartialShipment] = useState("");  // User must select
  
  // Animal usage purpose states
  const [internalResearch, setInternalResearch] = useState(false);
  const [commercialUse, setCommercialUse] = useState(false);
  const [contractResearch, setContractResearch] = useState(false);
  
  // Additional form states
  const [userEtc, setUserEtc] = useState("");
  const [deliveryEtc, setDeliveryEtc] = useState("");

  // Handle strain changes
  const handleStrainChange = (index: number, field: keyof AnimalStrain, value: string | number) => {
    setAnimalStrains(prev => prev.map((strain, i) => 
      i === index ? { ...strain, [field]: value } : strain
    ));
  };

  const addStrain = () => {
    setAnimalStrains(prev => [...prev, {
      strain_name: "",
      strain_number: "",
      genotype: "",
      quantity: "",
      sex: "",
      age_range: "",
      different_age_acceptable: "",
      product_etc: ""
    }]);
  };

  const removeStrain = (index: number) => {
    if (animalStrains.length > 1) {
      setAnimalStrains(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Expose all form data to parent component
  const getOrderData = () => {
    const purposes = [];
    if (internalResearch) purposes.push('internal-research');
    if (commercialUse) purposes.push('commercial-use');
    if (contractResearch) purposes.push('contract-research');
    
    return {
      // User Information
      piName,
      accountNumber,
      phoneNumber: `+82 ${phoneNumber1}-${phoneNumber2}-${phoneNumber3}`,
      email,
      organization,
      institutionType,
      institutionAddress,
      userEtc: userEtc || undefined,
      
      // Product Information - using first strain for backward compatibility
      strainName: animalStrains[0]?.strain_name || "",
      strainNumber: animalStrains[0]?.strain_number || "",
      genotype: animalStrains[0]?.genotype || undefined,
      quantity: animalStrains[0]?.quantity || undefined,
      sex: animalStrains[0]?.sex || undefined,
      ageRange: animalStrains[0]?.age_range || undefined,
      differentAgeAcceptable: animalStrains[0]?.different_age_acceptable || undefined,
      animalUsePurpose: purposes,
      productEtc: animalStrains[0]?.product_etc || undefined,
      
      // Multiple animal strains array
      animalStrains: animalStrains,
      
      // Delivery Information
      shippingAddress,
      deliveryDate,
      partialShipment,
      deliveryEtc: deliveryEtc || undefined,
    };
  };

  // Expose getOrderData function to parent component
  useEffect(() => {
    (window as any).getOrderData = getOrderData;
  }, [piName, accountNumber, phoneNumber1, phoneNumber2, phoneNumber3, email, organization, institutionType, institutionAddress, userEtc, animalStrains, internalResearch, commercialUse, contractResearch, shippingAddress, deliveryDate, partialShipment, deliveryEtc]);

  // Validation logic
  const getMissingFields = () => {
    const missing = [];
    if (piName.trim() === "") missing.push("연구책임자 이름");
    if (accountNumber.trim() === "") missing.push("계정번호");
    if (phoneNumber1.trim() === "" || phoneNumber2.trim() === "" || phoneNumber3.trim() === "") missing.push("전화번호");
    if (email.trim() === "") missing.push("이메일");
    if (organization.trim() === "") missing.push("기관명");
    if (institutionType.trim() === "") missing.push("기관 유형");
    if (institutionAddress.trim() === "") missing.push("기관 주소");
    
    // Validate ALL animal strains (not just the first one)
    animalStrains.forEach((strain, index) => {
      const strainNumber = index + 1;
      if (strain.strain_name.trim() === "") {
        missing.push(`계통 ${strainNumber} - 계통명`);
      }
      if (strain.strain_number.trim() === "") {
        missing.push(`계통 ${strainNumber} - 계통 번호`);
      }
    });
    
    if (shippingAddress.trim() === "") missing.push("배송지 주소");
    if (partialShipment.trim() === "") missing.push("부분 선적 허용여부");
    if (!internalResearch && !commercialUse && !contractResearch) missing.push("동물 사용 목적");
    return missing;
  };

  const isFormValid = () => {
    return getMissingFields().length === 0;
  };

  // Notify parent when validation state changes
  useEffect(() => {
    onValidChange(isFormValid());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piName, accountNumber, phoneNumber1, phoneNumber2, phoneNumber3, email, organization, institutionType, institutionAddress, animalStrains, shippingAddress, deliveryDate, partialShipment, internalResearch, commercialUse, contractResearch, onValidChange]);

  // Expose getMissingFields function to parent component
  useEffect(() => {
    (window as any).getStep2MissingFields = getMissingFields;
  }, [piName, accountNumber, phoneNumber1, phoneNumber2, phoneNumber3, email, organization, institutionType, institutionAddress, animalStrains, shippingAddress, deliveryDate, partialShipment, internalResearch, commercialUse, contractResearch]);

  const handleInstitutionTypeChange = (type: string) => {
    // Toggle behavior: if already selected, don't allow unselecting
    // If different option selected, switch to it
    if (institutionType === type) {
      // Don't allow unselecting - keep current value
      return;
    }
    setInstitutionType(type);
  };

  const handlePartialShipmentChange = (value: string) => {
    // Toggle behavior: if already selected, don't allow unselecting
    // If different option selected, switch to it
    if (partialShipment === value) {
      // Don't allow unselecting - keep current value
      return;
    }
    setPartialShipment(value);
  };

  const handleSameAsCustomerInfo = (checked: boolean) => {
    setSameAsCustomerInfo(checked);
    if (checked) {
      setShippingAddress(institutionAddress);
    } else {
      setShippingAddress("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-6 md:mb-10">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold">Step 2. 주문 정보 기입</h2>
        <span className="text-xs md:text-sm text-muted-foreground bg-muted px-2 md:px-3 py-1 rounded-md self-start md:self-auto">
          (<span className="text-destructive font-medium">영문으로</span> 기재해주세요)
        </span>
      </div>
      
      <form className="space-y-12">
        {/* User Information */}
        <div className="bg-gradient-to-br from-muted/30 to-muted/60 p-4 md:p-8 rounded-xl border border-border/50 shadow-sm">
          <div className="flex items-center gap-3 mb-4 md:mb-6 pb-3 border-b border-border/30">
            <div className="bg-primary/10 text-primary rounded-lg px-2 md:px-3 py-1 text-xs md:text-sm font-semibold">2-1</div>
            <h3 className="font-bold text-lg md:text-xl">사용자 (User)</h3>
          </div>
          <div className="space-y-4 md:space-y-3">
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                연구책임자 이름<br />
                <span className="text-muted-foreground">(Name of PI)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <Input 
                  placeholder="연구책임자 이름을 입력하세요" 
                  value={piName}
                  onChange={(e) => setPiName(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                계정번호<br />
                <span className="text-muted-foreground">(Account No.)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <Input 
                  placeholder="숫자로만 입력하세요" 
                  type="number" 
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                전화번호<br />
                <span className="text-muted-foreground">(Phone Number)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
                  <div className="bg-muted px-3 py-2 rounded-md text-sm font-medium">+82</div>
                  <div className="flex items-center gap-1 md:gap-2 flex-1 md:flex-none">
                    <Input 
                      placeholder="010" 
                      className="w-16 md:w-20 text-center" 
                      maxLength={3}
                      value={phoneNumber1}
                      onChange={(e) => setPhoneNumber1(e.target.value)}
                    />
                    <span className="text-muted-foreground text-sm">-</span>
                    <Input 
                      placeholder="1234" 
                      className="w-20 md:w-20 text-center" 
                      maxLength={4}
                      value={phoneNumber2}
                      onChange={(e) => setPhoneNumber2(e.target.value)}
                    />
                    <span className="text-muted-foreground text-sm">-</span>
                    <Input 
                      placeholder="5678" 
                      className="w-20 md:w-20 text-center" 
                      maxLength={4}
                      value={phoneNumber3}
                      onChange={(e) => setPhoneNumber3(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                이메일<br />
                <span className="text-muted-foreground">(Email)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <Input 
                  type="email" 
                  placeholder="이메일 주소를 입력하세요"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                기관명<br />
                <span className="text-muted-foreground">(Organization)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <Input 
                  placeholder="기관명을 입력하세요"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                기관 유형<br />
                <span className="text-muted-foreground">(Institution Type)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="profit" 
                    checked={institutionType === 'profit'}
                    onCheckedChange={() => handleInstitutionTypeChange('profit')}
                  />
                  <Label htmlFor="profit" className="text-sm font-medium">Profit</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="non-profit" 
                    checked={institutionType === 'non-profit'}
                    onCheckedChange={() => handleInstitutionTypeChange('non-profit')}
                  />
                  <Label htmlFor="non-profit" className="text-sm font-medium">Non-Profit</Label>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-start">
              <div className="md:col-span-3 text-sm font-medium md:pt-2">
                기관 주소<br />
                <span className="text-muted-foreground">(Address)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <Textarea 
                  placeholder="우편번호를 포함하여 입력하세요" 
                  className="min-h-[80px] resize-none"
                  value={institutionAddress}
                  onChange={(e) => setInstitutionAddress(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-start">
              <div className="md:col-span-3 text-sm font-medium md:pt-2">
                기타 사항<br />
                <span className="text-muted-foreground">(Etc.)</span>
              </div>
              <div className="md:col-span-8">
                 <Textarea 
                   placeholder="기타 사항을 입력하세요 (선택사항)" 
                   className="min-h-[80px] resize-none"
                   value={userEtc}
                   onChange={(e) => setUserEtc(e.target.value)}
                 />
              </div>
            </div>
          </div>
        </div>

        {/* Product Information */}
        <div className="bg-gradient-to-br from-muted/30 to-muted/60 p-4 md:p-8 rounded-xl border border-border/50 shadow-sm">
          <div className="flex items-center justify-between mb-4 md:mb-6 pb-3 border-b border-border/30">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 text-primary rounded-lg px-2 md:px-3 py-1 text-xs md:text-sm font-semibold">2-2</div>
              <h3 className="font-bold text-lg md:text-xl">제품 (Product)</h3>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addStrain}
              className="text-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              계통 추가
            </Button>
          </div>
          
          <div className="space-y-6">
            {animalStrains.map((strain, index) => (
              <div key={index} className="p-4 border rounded-lg bg-muted/20">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="font-medium">계통 {index + 1}</h5>
                  {animalStrains.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStrain(index)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Minus className="h-4 w-4 mr-1" />
                      삭제
                    </Button>
                  )}
                </div>
                
                <div className="space-y-4 md:space-y-3">
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
                    <div className="md:col-span-3 text-sm font-medium">
                      계통명<br />
                      <span className="text-muted-foreground">(Strain Name)</span> 
                      <span className="text-destructive"> *</span>
                    </div>
                    <div className="md:col-span-8">
                      <Input 
                        placeholder="계통명을 입력하세요"
                        value={strain.strain_name}
                        onChange={(e) => handleStrainChange(index, 'strain_name', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
                    <div className="md:col-span-3 text-sm font-medium">
                      계통 번호<br />
                      <span className="text-muted-foreground">(Strain Stock Number)</span>
                      <span className="text-destructive"> *</span>
                    </div>
                    <div className="md:col-span-8">
                      <Input 
                        placeholder="6자리 숫자를 입력하세요" 
                        maxLength={6}
                        value={strain.strain_number}
                        onChange={(e) => handleStrainChange(index, 'strain_number', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
                    <div className="md:col-span-3 text-sm font-medium">
                      유전자형<br />
                      <span className="text-muted-foreground">(Genotype)</span>
                    </div>
                    <div className="md:col-span-8">
                       <Input 
                         placeholder="유전자형을 입력하세요" 
                         value={strain.genotype}
                         onChange={(e) => handleStrainChange(index, 'genotype', e.target.value)}
                       />
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
                    <div className="md:col-span-3 text-sm font-medium">
                      수량<br />
                      <span className="text-muted-foreground">(Quantity)</span>
                    </div>
                    <div className="md:col-span-8">
                       <Input 
                         type="number" 
                         placeholder="수량을 입력하세요" 
                         min="1" 
                         value={strain.quantity}
                         onChange={(e) => handleStrainChange(index, 'quantity', e.target.value ? Number(e.target.value) : "")}
                       />
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
                    <div className="md:col-span-3 text-sm font-medium">
                      성별<br />
                      <span className="text-muted-foreground">(Sex)</span>
                    </div>
                     <div className="md:col-span-8 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
                       <div className="flex items-center gap-2">
                         <Checkbox 
                           id={`male-${index}`}
                           checked={strain.sex === 'M'}
                           onCheckedChange={() => handleStrainChange(index, 'sex', strain.sex === 'M' ? '' : 'M')}
                         />
                         <Label htmlFor={`male-${index}`} className="text-sm font-medium">M</Label>
                       </div>
                       <div className="flex items-center gap-2">
                         <Checkbox 
                           id={`female-${index}`}
                           checked={strain.sex === 'F'}
                           onCheckedChange={() => handleStrainChange(index, 'sex', strain.sex === 'F' ? '' : 'F')}
                         />
                         <Label htmlFor={`female-${index}`} className="text-sm font-medium">F</Label>
                       </div>
                       <div className="flex items-center gap-2">
                         <Checkbox 
                           id={`both-${index}`}
                           checked={strain.sex === 'Both'}
                           onCheckedChange={() => handleStrainChange(index, 'sex', strain.sex === 'Both' ? '' : 'Both')}
                         />
                         <Label htmlFor={`both-${index}`} className="text-sm font-medium">Both</Label>
                       </div>
                     </div>
                  </div>
                  
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
                    <div className="md:col-span-3 text-sm font-medium">
                      연령 범위 (단위: 주령)<br />
                      <span className="text-muted-foreground">(Desired Age Range, unit: weekage)</span>
                    </div>
                    <div className="md:col-span-8">
                       <Input 
                         placeholder="연령 범위를 입력하세요"
                         value={strain.age_range}
                         onChange={(e) => handleStrainChange(index, 'age_range', e.target.value)}
                       />
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
                    <div className="md:col-span-3 text-sm font-medium">
                      다른 주령 선적 허용 여부<br />
                      <span className="text-muted-foreground">(Different Age Acceptable)</span>
                    </div>
                     <div className="md:col-span-8 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
                       <div className="flex items-center gap-2">
                         <Checkbox 
                           id={`age-yes-${index}`}
                           checked={strain.different_age_acceptable === 'yes'}
                           onCheckedChange={() => handleStrainChange(index, 'different_age_acceptable', strain.different_age_acceptable === 'yes' ? '' : 'yes')}
                         />
                         <Label htmlFor={`age-yes-${index}`} className="text-sm font-medium">Yes</Label>
                       </div>
                       <div className="flex items-center gap-2">
                         <Checkbox 
                           id={`age-no-${index}`}
                           checked={strain.different_age_acceptable === 'no'}
                           onCheckedChange={() => handleStrainChange(index, 'different_age_acceptable', strain.different_age_acceptable === 'no' ? '' : 'no')}
                         />
                         <Label htmlFor={`age-no-${index}`} className="text-sm font-medium">No</Label>
                       </div>
                     </div>
                  </div>
                  
                  <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-start">
                    <div className="md:col-span-3 text-sm font-medium md:pt-2">
                      기타 사항<br />
                      <span className="text-muted-foreground">(Etc.)</span>
                    </div>
                    <div className="md:col-span-8">
                       <Textarea 
                         placeholder="기타 사항을 입력하세요" 
                         className="min-h-[80px] resize-none"
                         value={strain.product_etc}
                         onChange={(e) => handleStrainChange(index, 'product_etc', e.target.value)}
                       />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 border-t pt-6">
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                동물 사용 목적<br />
                <span className="text-muted-foreground">(Purpose of the Animal Use)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="internal-research" 
                      checked={internalResearch}
                      onCheckedChange={(checked) => setInternalResearch(checked as boolean)}
                    />
                    <Label htmlFor="internal-research" className="text-sm font-medium">자체 연구 (Internal Research)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="commercial-use" 
                      checked={commercialUse}
                      onCheckedChange={(checked) => setCommercialUse(checked as boolean)}
                    />
                    <Label htmlFor="commercial-use" className="text-sm font-medium">영리 목적 연구 (Commercial Use)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="contract-research" 
                      checked={contractResearch}
                      onCheckedChange={(checked) => setContractResearch(checked as boolean)}
                    />
                    <Label htmlFor="contract-research" className="text-sm font-medium">위탁/대행 연구 (Contract Research, CRO)</Label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Information */}
        <div className="bg-gradient-to-br from-muted/30 to-muted/60 p-4 md:p-8 rounded-xl border border-border/50 shadow-sm">
          <div className="flex items-center gap-3 mb-4 md:mb-6 pb-3 border-b border-border/30">
            <div className="bg-primary/10 text-primary rounded-lg px-2 md:px-3 py-1 text-xs md:text-sm font-semibold">2-3</div>
            <h3 className="font-bold text-lg md:text-xl">배송 (Delivery)</h3>
          </div>
          <div className="space-y-4 md:space-y-3">
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-start">
              <div className="md:col-span-3 text-sm font-medium md:pt-2">
                배송지 주소<br />
                <span className="text-muted-foreground">(Shipping Address)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="same-address" 
                      checked={sameAsCustomerInfo}
                      onCheckedChange={handleSameAsCustomerInfo}
                    />
                    <Label htmlFor="same-address" className="text-sm">고객 정보와 동일</Label>
                  </div>
                  <Textarea 
                    placeholder="배송받을 주소를 입력하세요" 
                    className="min-h-[80px] resize-none"
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                  />
                </div>
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                희망 선적일<br />
                <span className="text-muted-foreground">(Desired Shipment Date)</span>
              </div>
              <div className="md:col-span-8">
                <Input 
                  type="date" 
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-center">
              <div className="md:col-span-3 text-sm font-medium">
                부분 선적 허용여부<br />
                <span className="text-muted-foreground">(Partial Shipment Acceptable)</span> <span className="text-destructive">*</span>
              </div>
              <div className="md:col-span-8 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="partial-yes" 
                    checked={partialShipment === 'yes'}
                    onCheckedChange={() => handlePartialShipmentChange('yes')}
                  />
                  <Label htmlFor="partial-yes" className="text-sm font-medium">Yes</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="partial-no" 
                    checked={partialShipment === 'no'}
                    onCheckedChange={() => handlePartialShipmentChange('no')}
                  />
                  <Label htmlFor="partial-no" className="text-sm font-medium">No</Label>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col md:grid md:grid-cols-11 gap-2 md:gap-4 md:items-start">
              <div className="md:col-span-3 text-sm font-medium md:pt-2">
                기타 사항<br />
                <span className="text-muted-foreground">(Etc.)</span>
              </div>
              <div className="md:col-span-8">
                 <Textarea 
                   placeholder="배송 관련 기타 사항을 입력하세요 (선택사항)" 
                   className="min-h-[80px] resize-none"
                   value={deliveryEtc}
                   onChange={(e) => setDeliveryEtc(e.target.value)}
                 />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OrderFormStep;